/**
 * Model loading utilities.
 *
 * Supports three paths:
 *   1. OPFS cache — instant, no user interaction.
 *   2. Network download — fetches from MODEL_REGISTRY URLs, saves to OPFS.
 *   3. Local file picker — File System Access API / <input> fallback.
 */

import { downloadModel } from "./downloader.ts";
import { getCachedModel, saveCachedModel } from "./opfs-cache.ts";

// ── Model registry ─────────────────────────────────────────────────────────

const FILES_HOST = location.hostname.endsWith(".cn")
  ? "https://files.zhoukz.cn"
  : "https://files.cf.zhoukz.com";

export const MODEL_REGISTRY = {
  backbone: {
    name: "midashenglm-0.6b-backbone-Q4_K_M.gguf",
    url: `${FILES_HOST}/api/v1/files/midashenglm-0.6b-backbone-Q4_K_M.gguf`,
  },
  mmproj: {
    name: "midashenglm-0.6b-mmproj-f32.gguf",
    url: `${FILES_HOST}/api/v1/files/midashenglm-0.6b-mmproj-f32.gguf`,
  },
};

// ── OPFS cache loading ─────────────────────────────────────────────────────

export async function loadCachedModels(): Promise<{
  backbone: File;
  mmproj: File;
} | null> {
  const [backbone, mmproj] = await Promise.all([
    getCachedModel(MODEL_REGISTRY.backbone.name),
    getCachedModel(MODEL_REGISTRY.mmproj.name),
  ]);
  if (backbone && mmproj) return { backbone, mmproj };
  return null;
}

// ── Network download + cache ───────────────────────────────────────────────

export interface DownloadProgress {
  phase: "backbone" | "mmproj";
  loaded: number;
  total: number;
}

export async function downloadAndCacheModels(
  onProgress: (p: DownloadProgress) => void,
): Promise<{ backbone: File; mmproj: File }> {
  // Download backbone
  const backboneBlob = await downloadModel(
    MODEL_REGISTRY.backbone.url,
    (loaded, total) => onProgress({ phase: "backbone", loaded, total }),
  );
  await saveCachedModel(MODEL_REGISTRY.backbone.name, backboneBlob);

  // Download mmproj
  const mmprojBlob = await downloadModel(
    MODEL_REGISTRY.mmproj.url,
    (loaded, total) => onProgress({ phase: "mmproj", loaded, total }),
  );
  await saveCachedModel(MODEL_REGISTRY.mmproj.name, mmprojBlob);

  // Read back as File objects (with proper name / lastModified)
  const [backbone, mmproj] = await Promise.all([
    getCachedModel(MODEL_REGISTRY.backbone.name),
    getCachedModel(MODEL_REGISTRY.mmproj.name),
  ]);

  if (!backbone || !mmproj) {
    throw new Error("Failed to read cached models after download");
  }

  return { backbone, mmproj };
}

// ── Local file picker ──────────────────────────────────────────────────────

/**
 * Open a file picker for GGUF files and return the File object.
 *
 * Uses `showOpenFilePicker` when available (Chrome/Edge — allows persisting
 * the handle for next time), falls back to `<input type="file">`.
 */
export async function pickLocalFile(
  description = "GGUF model file",
): Promise<{ file: File; name: string }> {
  // Try modern File System Access API
  if ("showOpenFilePicker" in window) {
    const [handle] = await (window as any).showOpenFilePicker({
      types: [
        {
          description,
          accept: { "application/octet-stream": [".gguf"] },
        },
      ],
      multiple: false,
    });
    const file: File = await handle.getFile();
    return { file, name: file.name };
  }

  // Fallback: classic <input type="file">
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".gguf";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) {
        reject(new Error("No file selected"));
        return;
      }
      resolve({ file, name: file.name });
    };
    input.oncancel = () => reject(new Error("File selection cancelled"));
    input.click();
  });
}
